<script setup>
import SelectOptionFactory from '@/modules/select_option/SelectOptionFactory.js'
import { redirectAfterAuth } from '@/router/redirectAfterAuth'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { useCartStore } from '@/stores/cart'
import { useCustomerStore } from '@/stores/customer'
import { useNewCheckoutStore } from '@/stores/newCheckout'
import { useRegularCheckoutStore } from '@/stores/regularCheckout'
import { elb } from '@elbwalker/walker.js'
import { getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const cartStore = useCartStore()
const checkoutStore = useNewCheckoutStore()
const regularCheckoutStore = useRegularCheckoutStore()

const emits = defineEmits(['showLogin'])

const { appContext } = getCurrentInstance()
const translate = appContext.config.globalProperties.translate

const props = defineProps({
  emailRegex: RegExp
})

const router = useRouter()
const route = useRoute()

const title = ref(null)
const firstName = ref('')
const lastName = ref('')
const gender = ref('')
const email = ref('')
const password = ref('')
const consent = ref(false)
const showPassword = ref(false)
const loading = ref(false)
const form = ref(null)

// Refs for form fields to enable enter key navigation
const titleRef = ref(null)
const firstNameRef = ref(null)
const lastNameRef = ref(null)
const genderRef = ref(null)
const emailRef = ref(null)
const passwordRef = ref(null)
const consentRef = ref(null)
const submitButtonRef = ref(null)
const NR_SOURCE_AUTH_REGISTER = 'auth_register'
const titles = SelectOptionFactory.getTitleOptions()
const rules = {
  required: (value) => !!value || translate('validation_msg_required'),
  minLength: (value) =>
    (value?.length ?? 0) >= 8 || translate('validation_msg_min', 8),
  email: (value) =>
    props.emailRegex.test(value) || translate('validation_msg_email')
}

const el = ref(null)

onMounted(() => {
  elb(WALKER_COMMANDS.init, el.value)
})

const validateForm = async () => {
  const formInstance = form.value
  if (!formInstance) return false
  const validationResult = await formInstance.validate()
  return validationResult.valid
}

const _getCustomer = async (customer) => {
  const customerStore = useCustomerStore()
  const [{ default: UtilityHelper }, { default: MessageHelper }] =
    await Promise.all([
      import('@/services/helpers/UtilityHelper'),
      import('@/services/helpers/MessageHelper')
    ])

  if (!customerStore.customerActive) {
    try {
      const address = customer.billingAddress

      await customerStore.register({
        email: customer.email,
        password: customer.password,
        firstName: address.first_name,
        lastName: address.last_name,
        gender: address.gender,
        title: address.title
      })
    } catch (e) {
      if (e.status === 409) {
        UtilityHelper.noticeNRError(
          e,
          translate('Failed to register customer, email already exists')
        )
        MessageHelper.addErrorMessage(
          'register_error_email_exists',
          null,
          5000,
          e,
          NR_SOURCE_AUTH_REGISTER
        )
        return false
      } else {
        UtilityHelper.noticeNRError(e, 'Failed to register customer')
        MessageHelper.addErrorMessage(
          'validation_msg_email',
          null,
          5000,
          e,
          NR_SOURCE_AUTH_REGISTER
        )
      }
    }
  }

  return customerStore.customerActive
}

const storeUserDataForCheckout = (redirectUrl) => {
  if (!redirectUrl.includes('/checkout/v2')) return

  const addressData = {
    first_name: firstName.value,
    last_name: lastName.value,
    gender: gender.value,
    title: title.value
  }

  regularCheckoutStore.email = email.value
  regularCheckoutStore.selectedShippingAddressData = addressData
  regularCheckoutStore.selectedBillingAddressData = addressData
}

const registerAction = async () => {
  const isFormValid = await validateForm()
  if (!isFormValid) return

  loading.value = true

  const customer = {
    email: email.value,
    password: password.value,
    billingAddress: {
      first_name: firstName.value,
      last_name: lastName.value,
      gender: gender.value,
      title: title.value
    },
    shippingAddress: {
      first_name: firstName.value,
      last_name: lastName.value,
      gender: gender.value,
      title: title.value
    }
  }

  try {
    const registered = await _getCustomer(customer)
    if (registered) {
      await cartStore.refreshCart()

      const redirectUri = route.query.redirect_uri || '/'
      storeUserDataForCheckout(redirectUri)

      await redirectAfterAuth(router, redirectUri)
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div
    ref="el"
    class="d-flex flex-column justify-center"
    data-elb="account"
    data-elb-account="action:register"
    data-elbaction="visible"
  >
    <span class="text-center">
      {{ translate('auth_register_description') }}
    </span>
    <VForm
      ref="form"
      lazy-validation
      class="d-flex flex-column justify-center mt-5"
    >
      <VSelect
        ref="titleRef"
        v-model="title"
        :label="translate('opc_address_title_label')"
        :items="titles"
        item-value="value"
        item-title="title"
        clearable
        outlined
        class="pb-3"
        @keydown.enter.prevent="firstNameRef?.focus()"
      />

      <div class="d-flex flex-column flex-md-row py-3">
        <div class="d-flex flex-column flex-grow-1 pr-md-2 pr-0">
          <VTextField
            ref="firstNameRef"
            v-model="firstName"
            :rules="[rules.required]"
            :label="translate('opc_address_firstname_label')"
            autocomplete="given-name"
            @keydown.enter.prevent="lastNameRef?.focus()"
          />
        </div>

        <div class="d-flex flex-column flex-grow-1 pl-md-2 pl-0">
          <VTextField
            ref="lastNameRef"
            v-model="lastName"
            :rules="[rules.required]"
            :label="translate('opc_address_lastname_label')"
            autocomplete="family-name"
            @keydown.enter.prevent="emailRef?.focus()"
          />
        </div>
      </div>
      <div class="d-flex gap-4 py-3">
        <VRadioGroup
          ref="genderRef"
          :label="`${translate('opc_address_salutation_label')}:`"
          v-model="gender"
          :rules="[rules.required]"
          inline
          @keydown.enter.prevent="emailRef?.focus()"
        >
          <VRadio :label="translate('gender_female_label')" value="female" />
          <VRadio :label="translate('gender_male_label')" value="male" />
        </VRadioGroup>
      </div>

      <VTextField
        ref="emailRef"
        type="email"
        v-model="email"
        :label="translate('fe_app_label_email')"
        :rules="[rules.required, rules.email]"
        autocomplete="email"
        class="mt-3"
        @keydown.enter.prevent="passwordRef?.focus()"
      />

      <VTextField
        ref="passwordRef"
        class="mt-5"
        v-model="password"
        :label="translate('fe_app_label_password')"
        :rules="[rules.required, rules.minLength]"
        :append-inner-icon="showPassword ? 'custom:eyeOn' : 'custom:eyeOff'"
        :type="showPassword ? 'text' : 'password'"
        autocomplete="new-password"
        @click:append-inner="showPassword = !showPassword"
        @keydown.enter.prevent="
          consentRef?.$el?.querySelector('input[type=checkbox]')?.focus() ||
          registerAction()
        "
      />

      <VCheckbox
        ref="consentRef"
        v-model="consent"
        :rules="[(v) => v || translate('opc_agb_error')]"
        @keydown.enter.prevent="registerAction()"
      >
        <template v-slot:label>
          <span
            class="font-size-s mt-5 text-start"
            @click="checkForLink"
            v-html="translate('opc_agb_label')"
          />
        </template>
      </VCheckbox>

      <VBtn
        ref="submitButtonRef"
        color="primary"
        class="mt-5"
        @click="registerAction"
        :loading="loading"
        :disabled="loading"
        depressed
        block
      >
        {{ translate('header_navigation_register') }}
      </VBtn>
      <span class="mt-3 text-center">{{
        translate('auth_you_already_have_an_account')
      }}</span>
      <VBtnSecondary
        @click="emits('showLogin')"
        class="mt-3"
        :disabled="loading"
        block
      >
        {{ translate('login') }}
      </VBtnSecondary>
    </VForm>
  </div>
</template>

<style scoped>
:deep(.v-radio-group > .v-input__control > .v-label) {
  margin-inline-start: 0px;
}
</style>
